import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions, GridRow, GridRowButton, GridToolbarButton } from '@app/core/components/grid/grid.types';
import { DataTaskService } from '@app/core/services/data-task.service';
import { RememberService } from '@app/core/services/remember.service';
import { ProductsConfigParams } from '../products-config/products-config.component';
import { SelectLineModelParams } from '../select-line-model/select-line-model.component';

@Component({
    selector: 'tt-select-line-products',
    templateUrl: './select-line-products.component.html',
    styleUrls: ['./select-line-products.component.css'],
})
export class SelectLineProductsComponent implements OnChanges, OnInit {
    @Input()
    public params?: SelectLineProductsParams;

    public get?: SelectLineProductsGet;

    public grid?: GridOptions;

    public submitted = false;

    @Output()
    public getLoaded = new EventEmitter<SelectLineProductsGet>();

    @Output()
    public submit = new EventEmitter<ProductsConfigParams>();

    constructor(
        private datatask: DataTaskService,
        private remember: RememberService,
    ) {}

    private createGrid() {
        this.grid = new Grid()
            .setRememberId('w_orderlineps.grid')
            .setLoadDataFunction({ method: 3839, parameters: () => this.get })
            .setSpecialFunc({ buttons: [this.buildIncrementButton(), this.buildDecrementButton()] })
            .setCustomToolbarButtons([this.buildApplySelectionButton()]);
    }

    private async loadGet() {
        if (!!this.params) {
            this.get = await this.getSelectLineProducts(this.params);
            this.getLoaded.emit(this.get);
        }
    }

    private async init() {
        this.submitted = false;

        await this.loadGet();
        if (!this.grid) {
            this.createGrid();
        } else {
            this.grid.gridfunc?.read();
        }
    }

    private async getSelectLineProducts(params: SelectLineProductsParams): Promise<SelectLineProductsGet> {
        return (await this.datatask.Post(1098, params))[0];
    }

    private buildApplySelectionButton(): GridToolbarButton {
        return {
            name: 'applyselection',
            text: 'orderlineps_productsconfig',
            translate: true,
            icon: 'fas fa-check',
            cssClass: 'tt-button tt-button--success im-grid-btn-xs-l',
            disabled: () => !this.validSelection(),
            func: this.rememberSelectedProducts.bind(this),
            spinOnAction: true,
        };
    }

    private async rememberSelectedProducts() {
        if (!this.get) return;
        const rowsToRemember = (this.grid?.gridfunc?.getAllRows() ?? []).filter((row) => row['quantity'] > 0).map((row) => ({ item_id: row['item_id'], item_name: row['prod_name'], quantity: row['quantity'] }));

        await this.remember.remember('productsconfig_selected', btoa(JSON.stringify(rowsToRemember)));

        this.submit.emit({ argtype: this.get.argtype, headno: this.get.headno, lineno: this.get.lineno });
        this.submitted = true;
    }

    private validSelection(): boolean {
        const allRows = this.grid?.gridfunc?.getAllRows() ?? [];

        if (allRows.some((row) => isNaN(+row['quantity']) || +row['quantity'] < 0)) return false;

        if (allRows.some((row) => +row['quantity'] > 0)) {
            return true;
        }

        return false;
    }

    private buildIncrementButton(): GridRowButton {
        return { name: 'increment', tooltip: 'increment', translateTooltip: true, icon: 'fas fa-plus', btn_type: 'primary', func: (row: GridRow) => this.incrementRow(row) };
    }

    private buildDecrementButton(): GridRowButton {
        return { name: 'decrement', tooltip: 'decrement', translateTooltip: true, icon: 'fas fa-minus', btn_type: 'primary', func: (row: GridRow) => this.decrementRow(row), disabled: (row: GridRow) => (+row['quantity'] <= 0 ? true : false) };
    }

    private incrementRow(row: GridRow) {
        if (isNaN(+row['quantity'])) {
            row['quantity'] = 0;
        }

        row['quantity'] = +row['quantity'] + 1;
        this.grid?.gridfunc?.updateRow(row); //  (row);
        this.grid?.gridfunc?.refreshToolbarBtnDisability();

        this.submitted = false;
    }

    private decrementRow(row: GridRow) {
        if (isNaN(+row['quantity'])) {
            row['quantity'] = 0;
        }

        if (+row['quantity'] > 0) {
            row['quantity'] = +row['quantity'] - 1;
            this.grid?.gridfunc?.updateRow(row);
            this.grid?.gridfunc?.refreshToolbarBtnDisability();

            this.submitted = false;
        }
    }

    ngOnInit(): void {
        this.remember.remember('productsconfig_selected', '');
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']) {
            console.log('init again');
            this.init();
        }
    }
}

export interface SelectLineProductsParams extends SelectLineModelParams {
    // argtype: string;
    // headno: `${number}` | number;
    // lineno: `${number}` | number;
    selected_item_id: string;
}

export interface SelectLineProductsGet {
    argtype: 'orderline' | 'purchline';
    headno: `${number}` | number;
    lineno: `${number}` | number;
    selected_item_id: string;
    filtervalue: string;
    webpage_heading: string;
}
